import { Briefcase, Pencil, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '@/components/common/States'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import type { Position } from '../types'

type Props = {
  position: Position
  canWrite: boolean
  onEdit: () => void
}

export function PositionDetailPanel({ position, canWrite, onEdit }: Props) {
  const { t } = useTranslation()

  return (
    <section className="overflow-hidden rounded-2xl border bg-card">
      <div className="flex flex-wrap items-start gap-3 border-b p-4">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
          <Briefcase className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold">{position.title}</h2>
            <Badge tone="neutral">{t(`access.positions.levels.${position.level}`)}</Badge>
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">{position.description || t('access.positions.detail.noDescription')}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            <span className="font-mono">{position.code}</span>
            {position.department ? ` · ${position.department.name}` : ''}
          </p>
        </div>
        {canWrite ? (
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Pencil />
            {t('common.edit')}
          </Button>
        ) : null}
      </div>

      <div className="p-4">
        <h3 className="mb-3 text-sm font-semibold">{t('access.positions.detail.employeesTitle', { count: position.employees.length })}</h3>
        {position.employees.length === 0 ? (
          <EmptyState icon={Users} title={t('access.positions.detail.employeesEmpty')} />
        ) : (
          <Table>
            <THead>
              <TR className="hover:bg-transparent">
                <TH>{t('access.positions.detail.columns.name')}</TH>
                <TH className="hidden sm:table-cell">{t('access.positions.detail.columns.email')}</TH>
              </TR>
            </THead>
            <TBody>
              {position.employees.map((e) => (
                <TR key={e.id}>
                  <TD>
                    <div className="flex items-center gap-2">
                      <Avatar name={e.name} src={e.avatarUrl} className="size-8" />
                      <span className="font-medium">{e.name}</span>
                    </div>
                  </TD>
                  <TD className="hidden text-muted-foreground sm:table-cell">{e.email || '—'}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>
    </section>
  )
}
